<template>
  <div class="app">
    <div class="app-search">
      <div class="app-search-input">
        <el-input
          v-model="search.key"
          placeholder="商户名称/联系人/手机号"
          clearable
          @clear="searchList"
          @keyup.enter="searchList"
        />
        <el-select
          v-model="search.zt"
          placeholder="状态"
          clearable
          style="width: 120px; margin-left: 12px"
          @change="searchList"
        >
          <el-option label="全部" :value="null" />
          <el-option label="启用" :value="true" />
          <el-option label="禁用" :value="false" />
        </el-select>
      </div>
      <div class="app-search-btn">
        <el-button type="primary" @click="searchList">
          <el-icon><Search /></el-icon>查询
        </el-button>
        <el-button type="success" @click="openAdd">
          <el-icon><Plus /></el-icon>新增商户
        </el-button>
      </div>
    </div>

    <div class="app-table">
      <el-table
        :data="tableData.records"
        stripe
        v-loading="tableLoading"
        border
        style="width: 100%"
        row-key="id"
      >
        <el-table-column label="序号" type="index" width="60" align="center" />
        <el-table-column prop="mc" label="商户名称" min-width="160" show-overflow-tooltip />
        <el-table-column prop="zh" label="登录账号" width="130" />
        <el-table-column prop="lxr" label="联系人" width="100" />
        <el-table-column prop="sjhm" label="手机号码" width="130" />
        <el-table-column prop="zt" label="状态" width="80" align="center">
          <template #default="scope">
            <el-switch
              v-model="scope.row.zt"
              active-color="#13ce66"
              inactive-color="#ff4949"
              @change="updateStatus(scope.row)"
            />
          </template>
        </el-table-column>
        <el-table-column prop="bz" label="备注" min-width="120" show-overflow-tooltip />
        <el-table-column prop="cjsj" label="创建时间" width="160">
          <template #default="scope">
            {{ formatTime(scope.row.cjsj) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160" fixed="right" align="center">
          <template #default="scope">
            <el-button type="primary" link size="small" @click="openEdit(scope.row)">
              编辑
            </el-button>
            <el-button type="danger" link size="small" @click="deleteRow(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-if="tableData.total > 0"
        style="margin-top: 20px; justify-content: flex-end"
        background
        layout="total, prev, pager, next, sizes"
        :current-page="search.pageIndex"
        :page-size="search.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="tableData.total"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="560px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="formRules"
        label-width="100px"
        label-position="right"
      >
        <el-form-item label="商户名称" prop="mc">
          <el-input v-model="form.mc" placeholder="请输入商户名称" maxlength="50" />
        </el-form-item>
        <el-form-item label="登录账号" prop="zh" v-if="!isEdit">
          <el-input v-model="form.zh" placeholder="请输入登录账号" maxlength="30" />
        </el-form-item>
        <el-form-item label="登录密码" prop="mm" v-if="!isEdit">
          <el-input
            v-model="form.mm"
            type="password"
            placeholder="请输入登录密码"
            maxlength="30"
            show-password
          />
        </el-form-item>
        <el-form-item label="联系人" prop="lxr">
          <el-input v-model="form.lxr" placeholder="请输入联系人" maxlength="20" />
        </el-form-item>
        <el-form-item label="手机号码" prop="sjhm">
          <el-input v-model="form.sjhm" placeholder="请输入手机号码" maxlength="11" />
        </el-form-item>
        <el-form-item label="关联供应商">
          <el-input-number
            v-model="form.gysId"
            :min="1"
            placeholder="选填，关联供应商ID"
            controls-position="right"
            style="width:100%"
          />
          <span class="form-tip">关联供应商后，商户仅能核销该供应商的订单项</span>
        </el-form-item>
        <el-form-item label="备注" prop="bz" v-if="isEdit">
          <el-input
            v-model="form.bz"
            type="textarea"
            placeholder="选填"
            maxlength="200"
            :rows="2"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { Search, Plus } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { hxShList, hxShAdd, hxShEdit, hxShUpdateStatus, hxShDelete } from '@/http/api'

// ===== 搜索 =====
const search = reactive({
  key: '',
  zt: null,
  pageIndex: 1,
  pageSize: 20,
})

// ===== 表格 =====
const tableData = reactive({ records: [], total: 0 })
const tableLoading = ref(false)

function getParams() {
  return {
    Key: search.key || undefined,
    Zt: search.zt !== null && search.zt !== '' ? search.zt : undefined,
    PageIndex: search.pageIndex,
    PageSize: search.pageSize,
  }
}

async function getList() {
  tableLoading.value = true
  try {
    const res = await hxShList(getParams())
    tableData.records = res?.data?.records || res?.records || []
    tableData.total = res?.data?.total || res?.total || 0
  } catch {
    // handled by interceptor
  } finally {
    tableLoading.value = false
  }
}

function searchList() {
  search.pageIndex = 1
  getList()
}

function handleSizeChange(val) {
  search.pageSize = val
  search.pageIndex = 1
  getList()
}

function handleCurrentChange(val) {
  search.pageIndex = val
  getList()
}

function formatTime(time) {
  if (!time) return '--'
  return time.substring(0, 19).replace('T', ' ')
}

// ===== 状态切换 =====
async function updateStatus(row) {
  try {
    await hxShUpdateStatus(row.id, row.zt)
    ElMessage.success(row.zt === true ? '已启用' : '已禁用')
  } catch {
    row.zt = !row.zt // revert
  }
}

// ===== 新增/编辑弹窗 =====
const dialogVisible = ref(false)
const dialogTitle = ref('新增商户')
const isEdit = ref(false)
const editId = ref(null)
const submitLoading = ref(false)
const formRef = ref(null)

const form = reactive({
  mc: '',
  zh: '',
  mm: '',
  lxr: '',
  sjhm: '',
  gysId: null,
  bz: '',
})

const formRules = {
  mc: [{ required: true, message: '请输入商户名称', trigger: 'blur' }],
  zh: [{ required: true, message: '请输入登录账号', trigger: 'blur' }],
  mm: [{ required: true, message: '请输入登录密码', trigger: 'blur' }],
  sjhm: [
    { required: true, message: '请输入手机号码', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' },
  ],
}

function resetForm() {
  form.mc = ''
  form.zh = ''
  form.mm = ''
  form.lxr = ''
  form.sjhm = ''
  form.gysId = null
  form.bz = ''
}

function openAdd() {
  isEdit.value = false
  editId.value = null
  dialogTitle.value = '新增商户'
  resetForm()
  dialogVisible.value = true
}

function openEdit(row) {
  isEdit.value = true
  editId.value = row.id
  dialogTitle.value = '编辑商户 - ' + row.mc
  form.mc = row.mc
  form.lxr = row.lxr || ''
  form.sjhm = row.sjhm
  form.gysId = row.gysId ?? null
  form.bz = row.bz || ''
  dialogVisible.value = true
}

async function submitForm() {
  const valid = await formRef.value?.validate().catch(() => false)
  if (!valid) return

  submitLoading.value = true
  try {
    if (isEdit.value) {
      await hxShEdit(editId.value, {
        Mc: form.mc,
        Lxr: form.lxr,
        Sjhm: form.sjhm,
        GysId: form.gysId || undefined,
        Bz: form.bz,
      })
      ElMessage.success('编辑成功')
    } else {
      await hxShAdd({
        Mc: form.mc,
        Zh: form.zh,
        Mm: form.mm,
        Lxr: form.lxr,
        Sjhm: form.sjhm,
        GysId: form.gysId || undefined,
      })
      ElMessage.success('添加成功')
    }
    dialogVisible.value = false
    getList()
  } catch {
    // handled by interceptor
  } finally {
    submitLoading.value = false
  }
}

// ===== 删除 =====
function deleteRow(row) {
  ElMessageBox.confirm(`确认删除商户"${row.mc}"吗？删除后不可恢复。`, '删除确认', {
    type: 'warning',
  }).then(async () => {
    try {
      await hxShDelete(row.id)
      ElMessage.success('已删除')
      getList()
    } catch {
      // handled by interceptor
    }
  })
}

onMounted(() => {
  getList()
})
</script>

<style scoped>
.app {
  padding: 20px;
}
.app-search {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}
.app-search-input {
  display: flex;
  align-items: center;
  flex: 1;
}
.app-search-btn {
  display: flex;
  gap: 8px;
  margin-left: 16px;
}
.form-tip {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  line-height: 1.4;
}
</style>
